import * as React from "react";

import { cn } from "@/lib/utils";

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: "vertical" | "horizontal";
}

const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(
  ({ className, children, orientation = "vertical", ...props }, ref) => {
    const [showScrollbar, setShowScrollbar] = React.useState(false);
    const [scrollTop, setScrollTop] = React.useState(0);
    const [scrollLeft, setScrollLeft] = React.useState(0);
    const [scrollHeight, setScrollHeight] = React.useState(0);
    const [clientHeight, setClientHeight] = React.useState(0);
    const [scrollWidth, setScrollWidth] = React.useState(0);
    const [clientWidth, setClientWidth] = React.useState(0);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    const handleScroll = React.useCallback(() => {
      if (containerRef.current) {
        setScrollTop(containerRef.current.scrollTop);
        setScrollLeft(containerRef.current.scrollLeft);
      }
    }, []);

    const updateScrollInfo = React.useCallback(() => {
      if (containerRef.current && contentRef.current) {
        setScrollHeight(contentRef.current.scrollHeight);
        setClientHeight(containerRef.current.clientHeight);
        setScrollWidth(contentRef.current.scrollWidth);
        setClientWidth(containerRef.current.clientWidth);
        
        const needsVerticalScroll = contentRef.current.scrollHeight > containerRef.current.clientHeight;
        const needsHorizontalScroll = contentRef.current.scrollWidth > containerRef.current.clientWidth;
        setShowScrollbar(needsVerticalScroll || needsHorizontalScroll);
      }
    }, []);

    React.useEffect(() => {
      updateScrollInfo();
      window.addEventListener('resize', updateScrollInfo);
      return () => window.removeEventListener('resize', updateScrollInfo);
    }, [updateScrollInfo]);

    React.useEffect(() => {
      const resizeObserver = new ResizeObserver(updateScrollInfo);
      if (contentRef.current) {
        resizeObserver.observe(contentRef.current);
      }
      return () => resizeObserver.disconnect();
    }, [updateScrollInfo]);

    const verticalThumbHeight = clientHeight > 0 
      ? Math.max((clientHeight / scrollHeight) * clientHeight, 20)
      : 0;
    
    const horizontalThumbWidth = clientWidth > 0
      ? Math.max((clientWidth / scrollWidth) * clientWidth, 20)
      : 0;

    const verticalThumbTop = scrollHeight > clientHeight 
      ? (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - verticalThumbHeight)
      : 0;
    
    const horizontalThumbLeft = scrollWidth > clientWidth
      ? (scrollLeft / (scrollWidth - clientWidth)) * (clientWidth - horizontalThumbWidth)
      : 0;

    return (
      <div
        ref={ref}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        <div
          ref={containerRef}
          className="h-full w-full overflow-auto rounded-[inherit]"
          onScroll={handleScroll}
        >
          <div ref={contentRef} className="min-h-full min-w-full">
            {children}
          </div>
        </div>
        
        {/* Vertical Scrollbar */}
        {showScrollbar && scrollHeight > clientHeight && (
          <div className="absolute right-0 top-0 h-full w-2.5 border-l border-l-transparent p-[1px]">
            <div className="relative h-full w-full">
              <div
                className="absolute right-0 w-full rounded-full bg-border transition-colors"
                style={{
                  top: `${verticalThumbTop}px`,
                  height: `${verticalThumbHeight}px`,
                }}
              />
            </div>
          </div>
        )}

        {/* Horizontal Scrollbar */}
        {showScrollbar && scrollWidth > clientWidth && (
          <div className="absolute bottom-0 left-0 h-2.5 w-full border-t border-t-transparent p-[1px]">
            <div className="relative h-full w-full">
              <div
                className="absolute bottom-0 h-full rounded-full bg-border transition-colors"
                style={{
                  left: `${horizontalThumbLeft}px`,
                  width: `${horizontalThumbWidth}px`,
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);
ScrollArea.displayName = "ScrollArea";

export interface ScrollBarProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: "vertical" | "horizontal";
}

const ScrollBar = React.forwardRef<HTMLDivElement, ScrollBarProps>(
  ({ className, orientation = "vertical", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent p-[1px]",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-t border-t-transparent p-[1px]",
        className
      )}
      {...props}
    >
      <div className="relative flex-1 rounded-full bg-border" />
    </div>
  )
);
ScrollBar.displayName = "ScrollBar";

export { ScrollArea, ScrollBar };
